type ErrorReportingScope = {
  setTag: (key: string, value: string) => void;
  setContext: (key: string, context: Record<string, unknown>) => void;
};

type ErrorReportingClient = {
  withScope: (callback: (scope: ErrorReportingScope) => void) => void;
  captureException: (error: unknown) => void;
  captureMessage?: (
    message: string,
    options?: { level?: 'error' | 'warning'; extra?: Record<string, unknown> }
  ) => void;
  isInitialized?: () => boolean;
};

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

type LogPayload = {
  error?: Error;
  context?: Record<string, unknown>;
};

type CommerceApiErrorReport = {
  error: Error & {
    status: number;
    path: string;
    requestId: string | null;
    payload: unknown;
  };
  internalMessage: string | null;
};

let errorReportingClient: ErrorReportingClient | null = null;

export function setErrorReportingClient(client: ErrorReportingClient) {
  errorReportingClient = client;
}

function withReportingScope(
  configureScope: (scope: ErrorReportingScope) => void,
  report: (client: ErrorReportingClient) => void
) {
  if (typeof window === 'undefined') {
    return;
  }

  const client = errorReportingClient;
  if (!client || (client.isInitialized && !client.isInitialized())) {
    return;
  }

  client.withScope((scope) => {
    configureScope(scope);
    report(client);
  });
}

function captureWithScope(error: unknown, configureScope: (scope: ErrorReportingScope) => void) {
  withReportingScope(configureScope, (client) => {
    client.captureException(error);
  });
}

export function reportCommerceApiErrorToSentry({ error, internalMessage }: CommerceApiErrorReport) {
  captureWithScope(error, (scope) => {
    scope.setTag('commerce.status', String(error.status));
    scope.setTag('commerce.path', error.path);
    if (error.requestId) {
      scope.setTag('request_id', error.requestId);
    }
    scope.setContext('commerce_api', {
      path: error.path,
      status: error.status,
      requestId: error.requestId,
      internalMessage,
      payload: error.payload,
    });
  });
}

export function reportUnhandledUiErrorToSentry(error: unknown, context: Record<string, unknown>) {
  captureWithScope(error, (scope) => {
    scope.setTag('source', 'react-error-boundary');
    scope.setContext('react', context);
  });
}

export function reportLogToSentry(level: LogLevel, message: string, payload?: LogPayload) {
  if (level !== 'error' && level !== 'warn') {
    return;
  }

  withReportingScope(
    (scope) => {
      scope.setTag('log.level', level);
      scope.setContext('log', {
        message,
        ...payload?.context,
      });
    },
    (client) => {
      if (payload?.error) {
        client.captureException(payload.error);
        return;
      }

      if (client.captureMessage) {
        client.captureMessage(message, {
          level: level === 'warn' ? 'warning' : 'error',
          extra: payload?.context,
        });
        return;
      }

      client.captureException(new Error(message));
    }
  );
}
